/**
 * @fileoverview Hook UI Layer para gestión visual de direcciones de cliente en Inbox.
 * @module useCustomerAddressesUI
 * @category Hook - UI Layer
 * @since 3.2.0
 * @version 3.2.0
 */
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { SIDEBAR_ACTIONS } from '../../../../constants/actionsConstants';

/**
 * Hook UI Layer para manejar estado visual y handlers de CustomerAddressesMC.
 *
 * - Solo gestiona estado visual y handlers UI.
 * - Optimizado con useCallback y separación estricta de responsabilidades.
 * @param {Object} params - Parámetros del hook
 * @param {Object} params.customer - Objeto customer para el modal
 * @param {Function} params.loadAddresses - Función para recargar la lista de direcciones
 * @returns {Object} Estado visual y handlers UI
 */
export default function useCustomerAddressesUI({ customer, loadAddresses }) {
  // Acceso a dispatch de Redux
  const dispatch = useDispatch();

  /**
   * Abre el modal para crear una nueva dirección.
   * @memberof useCustomerAddressesUI
   */
  const openCreateAddress = useCallback((address = null) => {
    dispatch({
      type: SIDEBAR_ACTIONS.OPEN_SIDEBAR,
      payload: {
        componentKey: 'CustomerAddressSidebar',
        props: {
          customer,
          loadLocation: true,
          fakeAddress: false,
          hideNotification: false,
          selectedAddress: address,
          updateList: loadAddresses,
        },
      },
    });
  }, [dispatch, customer, loadAddresses]);

  /**
   * Abre el modal para editar una dirección existente.
   * @memberof useCustomerAddressesUI
   */
  const toggleEditAddress = useCallback((address) => {
    dispatch({
      type: SIDEBAR_ACTIONS.OPEN_SIDEBAR,
      payload: {
        componentKey: 'CustomerAddressSidebar',
        props: {
          customer,
          loadLocation: true,
          fakeAddress: false,
          hideNotification: false,
          selectedAddress: address,
          updateList: loadAddresses,
        },
      },
    });
  }, [dispatch, customer, loadAddresses]);

  return {
    openCreateAddress,
    toggleEditAddress,
  };
}
